'use client';

import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import GlobalBlockRenderer, {
  prefixClasses,
  processCustomClassesFromText,
} from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { StepsListTable } from '@/components/ContentBlocks/StepsList/StepsListTable';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { CustomShadows } from '@/theme';
import {
  ComponentBlocksStepItem,
  ComponentBlocksStepsList,
  Enum_Componentblocksstepslist_Stepsliststyle,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import { StepsListPlain } from './StepsListPlain';

export function StepsList({ blocks }: { blocks: ComponentBlocksStepsList }) {
  const theme = useTheme();
  const numColumns = useMemo(() => {
    const length = blocks.Steps?.length;
    if (length && [2, 3].includes(length)) {
      return {
        md: length,
        lg: length,
      };
    }
    const mod3 = (blocks.Steps?.length ?? 3) % 3;
    const mod4 = (blocks.Steps?.length ?? 4) % 4;
    return {
      md: mod3 !== 1 ? 3 : 2,
      lg: mod4 !== 1 ? 4 : 3,
    };
  }, [blocks.Steps]);

  if (
    !blocks.StepsListStyle ||
    blocks.StepsListStyle === Enum_Componentblocksstepslist_Stepsliststyle.Plain
  ) {
    return <StepsListPlain blocks={blocks} />;
  }

  if (
    [
      Enum_Componentblocksstepslist_Stepsliststyle.ImageTable,
      Enum_Componentblocksstepslist_Stepsliststyle.NumberedTable,
    ].includes(blocks.StepsListStyle)
  ) {
    return <StepsListTable blocks={blocks} />;
  }

  return (
    <CustomBackground
      backgroundOptions={(blocks?.StepsListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (blocks?.StepsListDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'StepsList',
      }}
    >
      <Box className={`steps-style-${blocks?.StepsListStyle}`} mx="auto">
        {(blocks?.Title || blocks?.StepsListBlurb) && (
          <Box
            sx={{
              mb: 2,
              maxWidth: 'sm',
              mx: 'auto',
              justifyItems: 'center',
              textAlign: 'center',
              '> *': {
                textAlign: 'center',
              },
            }}
          >
            {blocks?.Title && (
              <Typography
                variant="h2"
                textAlign="center"
                fontWeight={700}
                sx={{
                  color: theme.palette.common.black,
                  pb: { xs: 3, md: 4 },
                  px: 2,
                  maxWidth: '48rem',
                  mx: 'auto',
                  lineHeight: 1.25,
                }}
              >
                {blocks.Title}
              </Typography>
            )}
          </Box>
        )}

        {blocks?.StepsListBlurb && (
          <Box
            sx={{
              mb: 3,
            }}
          >
            <GlobalBlockRenderer content={blocks.StepsListBlurb || []} />
          </Box>
        )}

        <Box
          sx={{
            mt: blocks?.StepsListBlurb || blocks?.Title ? 4 : 0,
            '.steps-style-Card &, .steps-style-CardRow &': {
              display: 'grid',
              justifyContent: 'space-evenly',
              alignContent: 'space-evenly',
              columnGap: 4,
              rowGap: 4,
              px: 3,
              pb: 2,
            },
            '.steps-style-Card &': {
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: `repeat(${numColumns.md}, 1fr)`,
                xxl: `repeat(${numColumns.lg}, 1fr)`,
              },
            },
            '.steps-style-CardRow &': {
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: `repeat(${numColumns.lg}, 1fr)`,
              },
            },
            '.steps-style-BlueList &': {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'stretch',
              alignItems: 'center',
              gap: 3,
              pt: theme.spacing(2),
              pb: theme.spacing(6),
            },
          }}
        >
          {blocks.Steps?.map((step, index) => (
            <Step
              key={index}
              step={
                blocks?.StepsListShowNumbers === false
                  ? undefined
                  : {
                      stepNo: index + 1,
                      totalSteps: blocks.Steps!.length,
                      isLast: index === blocks.Steps!.length - 1,
                    }
              }
              StepsListStyle={blocks?.StepsListStyle ?? undefined}
              StepsListShowNumbers={blocks?.StepsListShowNumbers ?? undefined}
              {...(step as ComponentBlocksStepItem)}
            />
          ))}
        </Box>
      </Box>
    </CustomBackground>
  );
}

function Step({
  Title,
  Description,
  Image,
  step = undefined,
  StepsListStyle = undefined,
  StepsListShowNumbers = false,
}: ComponentBlocksStepItem & {
  step:
    | {
        stepNo: number;
        totalSteps: number;
        isLast: boolean;
      }
    | undefined;
  StepsListStyle?: Enum_Componentblocksstepslist_Stepsliststyle | undefined;
  StepsListShowNumbers?: boolean | undefined;
}) {
  const theme = useTheme();
  const effectiveStyle = StepsListStyle === 'CardRow' ? 'Card' : StepsListStyle;

  return (
    <Paper
      variant={'elevation'}
      data-component="StepItem"
      className={`step-style-${effectiveStyle}`}
      sx={{
        position: 'relative',
        mx: 'auto',
        '&.step-style-Card': {
          py: 2,
          px: 2,
          width: '100%',
          maxWidth: {
            xs: `calc(95% - ${theme.spacing(4)})`,
            sm: `calc(95% - ${theme.spacing(6)})`,
          },
          borderRadius: 6,
          boxShadow: (theme.shadows as CustomShadows).b,
          background: theme.palette.gradients.purpleWhite,
        },
        '&.step-style-BlueList': {
          border: 'none',
          background: theme.palette.gradients.primaryDark,
          width: '28rem',
          maxWidth: `calc(100% - ${theme.spacing(10)})`,
          display: 'flex',
          gap: theme.spacing(4),
          p: theme.spacing(3, 6, 3, 3),
          borderRadius: 6,
          '& > h1, & > h2, & > h3, & > h4, & > h5, & > h6': {
            marginTop: 0,
          },
          '.step-image': {
            width: '80px',
            height: '80px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            },
          },
          '.step-description': {
            flexGrow: 1,
            '& > p': { textAlign: 'left' },
          },
        },
      }}
    >
      <Box
        sx={{
          '.step-style-Card &': {
            position: 'absolute',
            left: '1rem',
            top: '0.75rem',
            fontWeight: 700,
            color: theme.palette.purple[500],
          },
          '.step-style-BlueList &': {
            position: 'absolute',
            right: '1rem',
            top: '1rem',
            fontWeight: 600,
            color: theme.palette.brand.secondary,
          },
        }}
      >
        <Typography
          sx={{
            fontSize:
              effectiveStyle === 'BlueList'
                ? 'clamp(1.25rem, 0.5vw + 1rem, 1.75rem)'
                : { xs: 36, sm: 42 },
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {step &&
            (step.stepNo < 10 && effectiveStyle === 'BlueList'
              ? `0${step.stepNo}`
              : `${step?.stepNo}`)}
        </Typography>
      </Box>

      {Image && (
        <Box
          sx={{
            textAlign: 'center',
            width: '90px',
            height: '90px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mt: 1,
            mb: 2,
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            },
          }}
          className="step-image"
        >
          <StrapiImage image={Image} size="logo" imageWidth={90} imageHeight={90} />
        </Box>
      )}

      <Box>
        {Title &&
          (() => {
            const { classes, cleanedText } = processCustomClassesFromText(Title);
            return (
              <Typography
                variant="h5"
                component="h3"
                className={classes.length > 0 ? prefixClasses(classes) : undefined}
                sx={{
                  color:
                    effectiveStyle === 'BlueList'
                      ? theme.palette.common.white
                      : theme.palette.text.primary,
                  mb: 1,
                  px: effectiveStyle === 'Card' && StepsListShowNumbers ? 0 : 0,
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
              >
                {cleanedText}
              </Typography>
            );
          })()}
        {Description && (
          <Box
            sx={{
              color:
                effectiveStyle === 'BlueList'
                  ? theme.palette.common.white
                  : theme.palette.text.secondary,
              '> p': {
                lineHeight: 1.5,
              },
              '> *:first-child': {
                mt: 0,
              },
              '> *:last-child': {
                mb: 0,
              },
            }}
            className="step-description"
          >
            <GlobalBlockRenderer content={Description} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}
